// Call-specific Sidetone line identity (UX spec 3.3, 6.3): renders the actual workspace phone
// number bound to a call alongside its catalog label and 8 px hue swatch.
//
// Provenance and contract rules:
// - Outbound calls resolve call-bound data (`call.from.e164` and `call.from.number_id`) before
//   consulting the workspace numbers catalog. The catalog label is supporting metadata, never a
//   substitute for the actual phone number used.
// - If the catalog row is missing, unassigned, or not readable by the viewer, the call's bound
//   phone number remains visible. A number or label is never inferred from a person's display
//   name, participant roster, or current number assignment.
// - The resolved line represents the selected Sidetone line recorded on the call, not proof of
//   carrier-delivered caller ID (ANI).
// - Internal calls have no physical PSTN phone number and never render a line identity.
// - Inbound `call.from.e164` is the external caller, never our workspace line. When the canonical
//   inbound workspace line is absent from the Call payload, `resolveCallLine` returns null (and
//   `inspectCallLine` reports `inbound_line_not_in_contract`) rather than reusing caller E.164.
import type { Call } from '@bcio-voice/contracts';
import type { CountryCode } from 'libphonenumber-js';
import type { CSSProperties } from 'react';
import { DEFAULT_COUNTRY, formatPhone } from '../lib/format.ts';
import { numberTagLabel } from '../lib/numberTags.ts';
import { TagSwatch } from './NumberTag.tsx';

export interface CallLineCatalogEntry {
  id: string;
  e164?: string | null | undefined;
  label?: string | null | undefined;
}

export type CallLineInput = Pick<Call, 'direction' | 'from'> & {
  destination?: Call['destination'] | null | undefined;
};

export interface ResolvedCallLine {
  /** Workspace number ID bound to the call, or null when only call-bound E.164 is present. */
  numberId: string | null;
  /** Stable key used to derive the 8-hue tag swatch (`numberId ?? e164`). */
  hueId: string;
  /** Catalog label when present and distinct from the phone number itself. */
  label: string | null;
  /** Unfiltered trimmed catalog label before number-as-label deduplication. */
  rawLabel: string | null;
  /** Canonical E.164 of the workspace line (never the remote party or external inbound caller). */
  e164: string | null;
  /** Formatted phone number from `formatPhone`, or null when E.164 is absent. */
  formattedNumber: string | null;
  /** Call direction ('outbound' | 'inbound'); internal calls never resolve a PSTN line. */
  direction: 'outbound' | 'inbound';
  /** Provenance of the line identity: always the selected/routed Sidetone line on the call record. */
  provenance: 'selected_sidetone_line';
  /** Explicit guard: selected Sidetone line is not proof of carrier-delivered caller ID. */
  carrierCallerIdProven: false;
  /** Which field supplied the line E.164. */
  e164Source: 'call_from_e164' | 'catalog_number_id' | 'inbound_destination_pstn' | 'absent';
  /** Whether `numberId` matched a row in the provided workspace numbers catalog. */
  catalogMatched: boolean;
}

export type CallLineInspection =
  | { status: 'resolved'; line: ResolvedCallLine }
  | { status: 'internal_no_pstn_line' }
  | { status: 'inbound_line_not_in_contract'; callerE164: string | null }
  | { status: 'outbound_line_unrecorded' };

const CONTAINER_STYLE: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  columnGap: '6px',
  rowGap: '2px',
  minWidth: 0,
  maxWidth: '100%',
};

const BADGE_STYLE: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  minWidth: 0,
  maxWidth: '100%',
};

const LABEL_STYLE: CSSProperties = {
  whiteSpace: 'normal',
  overflow: 'visible',
  textOverflow: 'clip',
  overflowWrap: 'anywhere',
};

const NUMBER_STYLE: CSSProperties = {
  whiteSpace: 'nowrap',
};

function cleanString(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function normalizeComparableDigits(value: string): string {
  const digits = value.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) return digits.slice(1);
  return digits;
}

/**
 * True when a catalog label is already the phone number (for example "(669) 310-0200",
 * "+16693100200", "669-310-0200", or "Line 669-310-0200"), so rendering both would duplicate it.
 */
export function isLabelSameAsNumber(
  label: string | null | undefined,
  e164: string | null | undefined,
  formattedNumber: string | null | undefined,
  country: CountryCode = DEFAULT_COUNTRY,
): boolean {
  const trimmed = cleanString(label);
  if (!trimmed) return false;
  const cleanE164 = cleanString(e164);
  const cleanFormatted = cleanString(formattedNumber) ?? (cleanE164 ? formatPhone(cleanE164, country) : null);
  if (cleanE164 && trimmed.toLowerCase() === cleanE164.toLowerCase()) return true;
  if (cleanFormatted && trimmed.toLowerCase() === cleanFormatted.toLowerCase()) return true;
  if (cleanE164) {
    const formattedLabel = formatPhone(trimmed, country);
    if (formattedLabel === cleanFormatted && formattedLabel !== trimmed) return true;
  }
  const withoutLinePrefix = trimmed.replace(/^line\s+/i, '');
  if (/[a-z]/i.test(withoutLinePrefix)) return false;
  const labelDigits = normalizeComparableDigits(withoutLinePrefix);
  if (labelDigits.length < 7) return false;
  const targetDigits = cleanE164
    ? normalizeComparableDigits(cleanE164)
    : cleanFormatted
      ? normalizeComparableDigits(cleanFormatted)
      : '';
  return targetDigits.length >= 7 && labelDigits === targetDigits;
}

/**
 * Inspects a call and optional workspace numbers catalog to classify its Sidetone line identity
 * without fabricating numbers for internal calls or confusing inbound caller E.164 with our line.
 */
export function inspectCallLine(
  call: CallLineInput | null | undefined,
  numbers?: readonly CallLineCatalogEntry[] | null,
  country: CountryCode = DEFAULT_COUNTRY,
): CallLineInspection {
  if (!call || call.direction === 'internal') {
    return { status: 'internal_no_pstn_line' };
  }

  const numberId = cleanString(call.from?.number_id);
  const catalog = numberId && Array.isArray(numbers) ? numbers.find(n => n.id === numberId) ?? null : null;
  const rawLabel = cleanString(catalog?.label);
  const catalogE164 = cleanString(catalog?.e164);

  if (call.direction === 'inbound') {
    // Inbound `call.from.e164` is the external caller's telephone number, never our Sidetone line.
    const callerE164 = cleanString(call.from?.e164);
    const destPstnE164 = call.destination?.kind === 'pstn' ? cleanString(call.destination.e164) : null;
    if (!numberId && !destPstnE164) {
      return { status: 'inbound_line_not_in_contract', callerE164 };
    }
    const e164 = catalogE164 ?? destPstnE164 ?? null;
    const e164Source: ResolvedCallLine['e164Source'] = catalogE164
      ? 'catalog_number_id'
      : destPstnE164
        ? 'inbound_destination_pstn'
        : 'absent';
    const formattedNumber = e164 ? formatPhone(e164, country) : null;
    const label = rawLabel && !isLabelSameAsNumber(rawLabel, e164, formattedNumber, country) ? rawLabel : null;
    const hueId = numberId ?? e164!;
    return {
      status: 'resolved',
      line: {
        numberId,
        hueId,
        label,
        rawLabel,
        e164,
        formattedNumber,
        direction: 'inbound',
        provenance: 'selected_sidetone_line',
        carrierCallerIdProven: false,
        e164Source,
        catalogMatched: !!catalog,
      },
    };
  }

  // Outbound: prefer call-bound `call.from.e164` and `call.from.number_id` before catalog lookup.
  // Never infer from `call.destination.e164` (remote party), `call.from.display_name`, or user assignments.
  const callFromE164 = cleanString(call.from?.e164);
  if (!callFromE164 && !numberId) {
    return { status: 'outbound_line_unrecorded' };
  }
  const e164 = callFromE164 ?? catalogE164 ?? null;
  const e164Source: ResolvedCallLine['e164Source'] = callFromE164
    ? 'call_from_e164'
    : catalogE164
      ? 'catalog_number_id'
      : 'absent';
  const formattedNumber = e164 ? formatPhone(e164, country) : null;
  const label = rawLabel && !isLabelSameAsNumber(rawLabel, e164, formattedNumber, country) ? rawLabel : null;
  const hueId = numberId ?? e164!;
  return {
    status: 'resolved',
    line: {
      numberId,
      hueId,
      label,
      rawLabel,
      e164,
      formattedNumber,
      direction: 'outbound',
      provenance: 'selected_sidetone_line',
      carrierCallerIdProven: false,
      e164Source,
      catalogMatched: !!catalog,
    },
  };
}

/**
 * Resolves the Sidetone line identity for a call, or returns null when the call has no bound
 * workspace line (internal calls, inbound calls without a workspace line field, or unrecorded).
 */
export function resolveCallLine(
  call: CallLineInput | null | undefined,
  numbers?: readonly CallLineCatalogEntry[] | null,
  country: CountryCode = DEFAULT_COUNTRY,
): ResolvedCallLine | null {
  const inspection = inspectCallLine(call, numbers, country);
  return inspection.status === 'resolved' ? inspection.line : null;
}

export interface CallLineIdentityProps {
  /** Pre-resolved call line from `resolveCallLine`. */
  line?: ResolvedCallLine | null | undefined;
  /** Call to resolve when `line` is not passed directly. */
  call?: CallLineInput | null | undefined;
  /** Workspace numbers catalog used when resolving `call`. */
  numbers?: readonly CallLineCatalogEntry[] | null | undefined;
  /** Optional country code for national vs international phone formatting (defaults to US). */
  country?: CountryCode | undefined;
  /** When true, renders truthful status copy if no workspace line is bound on `call`. */
  showUnrecordedFallback?: boolean | undefined;
}

export function CallLineIdentity({
  line,
  call,
  numbers,
  country = DEFAULT_COUNTRY,
  showUnrecordedFallback = false,
}: CallLineIdentityProps) {
  const resolved = line !== undefined ? line : resolveCallLine(call, numbers, country);
  if (!resolved) {
    if (!showUnrecordedFallback || !call) return null;
    const inspection = inspectCallLine(call, numbers, country);
    if (inspection.status === 'internal_no_pstn_line') {
      return <span className="call-line-identity meta">Internal, no phone number used</span>;
    }
    return <span className="call-line-identity meta">Line not recorded</span>;
  }

  const { hueId, label, formattedNumber } = resolved;

  if (label && formattedNumber) {
    return (
      <span className="number-tag call-line-identity" style={CONTAINER_STYLE}>
        <span className="call-line-badge" style={BADGE_STYLE}>
          <TagSwatch numberId={hueId} />
          <span className="number-tag-label call-line-label" style={LABEL_STYLE}>
            {label}
          </span>
        </span>
        {' '}
        <span className="sep" aria-hidden="true" />
        {' '}
        <span className="call-line-number mono tabular" style={NUMBER_STYLE}>
          {formattedNumber}
        </span>
      </span>
    );
  }

  if (formattedNumber) {
    return (
      <span className="number-tag call-line-identity" style={CONTAINER_STYLE}>
        <span className="call-line-badge" style={BADGE_STYLE}>
          <TagSwatch numberId={hueId} />
          <span className="call-line-number mono tabular" style={NUMBER_STYLE}>
            {formattedNumber}
          </span>
        </span>
      </span>
    );
  }

  return (
    <span className="number-tag call-line-identity" style={CONTAINER_STYLE}>
      <span className="call-line-badge" style={BADGE_STYLE}>
        <TagSwatch numberId={hueId} />
        <span className="number-tag-label call-line-label" style={LABEL_STYLE}>
          {numberTagLabel({ label })}
        </span>
      </span>
    </span>
  );
}
